/* eslint-disable react/no-unused-prop-types */
import React from 'react'
import PropTypes from 'prop-types'
import DialogActions from '@material-ui/core/DialogActions'
import Paper from '@material-ui/core/Paper'
import Draggable from 'react-draggable'
import { useSelector } from 'react-redux'

import { handleOnConnect, handleHMR, useConnect } from '../virtusa-redux'
import { informationDialogThemeOverride } from '../themes'
import { BaseDialog, ThemeButton } from '../components/Controls'

const reducerKey = 'informationDialog'
const dialogId = 'draggable-dialog-title'
const defaultButtonVariant = 'text'
const defaultButtonColor = 'primary'
const defaultButtonSize = 'medium'

// Define the redux initial state at the reducer key
const initialState = {
  id: reducerKey,
  isOpen: false,
  title: '',
  message: '',
  useCancel: false,
  cancelText: 'dialogs.cancel',
  okText: 'dialogs.ok',
  onCancel: () => {},
  onOk: () => {},
  buttons: null,
  okButtonVariant: defaultButtonVariant,
  okButtonColor: defaultButtonColor,
  okButtonSize: defaultButtonSize,
  cancelButtonVariant: defaultButtonVariant,
  cancelButtonColor: defaultButtonColor,
  cancelButtonSize: defaultButtonSize,
  closeX: false,
  dividers: false,
  translateMessage: false,
  noTranslateTitle: false,
  noTranslateButtons: false,
  primaryTitle: false,
  draggable: false,
}

let { setState, getState } = handleHMR(module, initialState)

const onConnect = store =>
  ({ setState, getState } = handleOnConnect(store, { reducerKey, initialState }))

export const informationDialogSelector = () =>
  getState()

// See initialState for the options.
export const openInformationDialog = arg => {
  if (typeof arg === 'string') {
    arg = { message: arg }
  }

  setState({ ...arg, isOpen: true })
}

export const closeInformationDialog = isOk => {
  setState(initialState)
}

export const openConfirmationDialog = arg => {
  if (typeof arg === 'string') {
    arg = { message: arg }
  }

  setState({ useCancel: true, ...arg, isOpen: true })
}

export const closeConfirmationDialog = isOk => {
  setState(initialState)
}

/**
 * PaperComponent component allows the dialog to be moved around from the title block.
 *
 * @component
 * @param {object} props component props
 * @returns {PaperComponent} PaperComponent component
 */
function PaperComponent(props) {
  return (
    <Draggable handle={`#${dialogId}`} cancel={'[class*="MuiDialogContent-root"]'}>
      <Paper {...props} />
    </Draggable>
  )
}

/**
 * InformationDialog component is a dialog used to display information to the user.
 *
 * @component
 * @returns {InformationDialog} InformationDialog component
 */
export default function InformationDialog() {
  useConnect(onConnect)
  const dialogObj = useSelector(informationDialogSelector)

  const handleCancel = () => {
    closeInformationDialog()
    dialogObj.onCancel && dialogObj.onCancel() 
  }

  const handleOk = () => {
    closeInformationDialog()
    dialogObj.onOk && dialogObj.onOk() 
  }

  const DialogButtons = () => {
    if (dialogObj.buttons) {
      return dialogObj.buttons
    }
    return (
      <DialogActions>
        {dialogObj.useCancel &&
        <ThemeButton noTranslate={dialogObj.noTranslateButtons} size={dialogObj.cancelButtonSize} color={dialogObj.cancelButtonColor} variant={dialogObj.cancelButtonVariant} autoFocus onClick={handleCancel}>
          {dialogObj.cancelText}
        </ThemeButton>
        }
        <ThemeButton noTranslate={dialogObj.noTranslateButtons} size={dialogObj.okButtonSize} color={dialogObj.okButtonColor} variant={dialogObj.okButtonVariant} onClick={handleOk}>
          {dialogObj.okText}
        </ThemeButton>
      </DialogActions>
    )
  }

  if (!dialogObj.isOpen) {
    return <></>
  }

  return (
    <BaseDialog
      id={dialogObj.id}
      isOpen={dialogObj.isOpen}
      dialogId={dialogId}
      message={dialogObj.message} 
      handleCancel={handleCancel}
      themeOverride={informationDialogThemeOverride}
      title={dialogObj.title}
      buttons={DialogButtons}
      closeX={dialogObj.closeX}
      dividers={dialogObj.dividers}
      translateMessage={dialogObj.translateMessage}
      noTranslateTitle={dialogObj.noTranslateTitle}
      noTranslateButtons={dialogObj.noTranslateButtons}
      primaryTitle={dialogObj.primaryTitle}
      draggable={dialogObj.draggable}
    />
  )
}

InformationDialog.propTypes = {
  /**
   Indicates whether the dialog should be open.
   */
  isOpen: PropTypes.bool,
  /**
   Dialog title.
   */
  title: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
    PropTypes.func
  ]),
  /**
   Dialog message.
   */
  message: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
    PropTypes.func
  ]),
  /**
   Use a cancel button.
   */
  useCancel: PropTypes.bool,
  /**
   Override the text for the cancel button.
   */
  cancelText: PropTypes.string,
  /**
   Override the text for the ok button.
   */
  okText: PropTypes.string,
  /**
   Execute onOk when the ok button is clicked and close the dialog.
   */
  onOk: PropTypes.func,
  /**
   Execute onCancel when the cancel button is clicked and close the dialog.
   */
  onCancel: PropTypes.func,
  /**
   Override the default buttons for the dialog.
   */
  buttons: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.func
  ]),
  /**
   Variant for the ok button.
   */
  okButtonVariant: PropTypes.oneOf(['contained', 'outlined', 'text']),
  /**
   Color for the ok button.
   */
  okButtonColor: PropTypes.oneOf(['primary', 'secondary', '']),
  /**
   Size for the ok button.
   */
  okButtonSize: PropTypes.oneOf(['small', 'medium', 'large']),
  /**
   Variant for the cancel button.
   */
  cancelButtonVariant: PropTypes.oneOf(['contained', 'outlined', 'text']),
  /**
   Color for the cancel button.
   */
  cancelButtonColor: PropTypes.oneOf(['primary', 'secondary', '']),
  /**
   Size for the cancel button.
   */
  cancelButtonSize: PropTypes.oneOf(['small', 'medium', 'large']),
  /**
   Use a close button at the top right of the dialog.
   */
  closeX: PropTypes.bool,
  /**
   Provide dividers for the message content.
   */
  dividers: PropTypes.bool,
  /**
   Translate the message (Must be a string).
   */
  translateMessage: PropTypes.bool,
  /**
   Prevents the language translation of the title.
   */
  noTranslateTitle: PropTypes.bool,
  /**
   Prevents the language translation of the button text when you override them.
   */
  noTranslateButtons: PropTypes.bool,
  /**
   Exported function from this module.<br/>
   Call this function to display an information dialog.<br/>
   Use the above props in an object as a parameter to the function call in order to configure the dialog.<br/>
   */
  openInformationDialog: PropTypes.func,
  /**
   * Makes the dialog draggable.
   */
  draggable: PropTypes.bool,
}

InformationDialog.defaultProps = {
  isOpen: false,
  buttons: null,
  okButtonVariant: 'text',
  okButtonColor: 'primary',
  okButtonSize: 'medium',
  cancelButtonVariant: 'text',
  cancelButtonColor: 'primary',
  cancelButtonSize: 'medium',
  okText: 'dialogs.ok',
  cancelText: 'dialogs.cancel',
  closeX: false,
  dividers: false,
  translateMessage: false,
  noTranslateTitle: false,
  noTranslateButtons: false
}

